import React from 'react';
import PropTypes from 'prop-types';
import ExpandableSection from '../../expandableSection';
import auditCountIcon from '../../../../../Assets/images/svg/audit_count.svg';
import averageIcon from '../../../../../Assets/images/svg/average.svg';
import lastAuditIcon from '../../../../../Assets/images/svg/last_audit.svg';
import { ActionButtons, DateCell } from './sectionShared';

const getAuditScoreClass = (score) => {
  const value = Number(score);
  if (!Number.isFinite(value)) return '';
  if (value >= 8) return 'audit-score-green';
  if (value >= 7) return 'audit-score-amber';
  return 'audit-score-red';
};

const formatAuditScoreDisplay = (score) => {
  if (score === null || score === undefined || score === '') return '-';
  const str = String(score).trim();
  if (str.toUpperCase() === 'NA') return 'NA';
  if (str === '-') return '-';
  const num = Number(str);
  return Number.isFinite(num) ? num.toFixed(2) : str;
};

const SummaryCard = ({ title, data }) => (
  <div className="summary-card">
    <div className="summary-header">{title}</div>
    <div className="summary-body audit-summary">
      <div className="summary-metric">
        <div className="metric-icon">
          <img src={auditCountIcon} alt="Audit count icon" className="metric-icon-image" />
        </div>
        <div className="metric-text">
          <span>Audit count</span>
          <strong>{data?.count || 0}</strong>
        </div>
      </div>
      <div className="metric-divider" />
      <div className="summary-metric">
        <div className="metric-icon">
          <img src={averageIcon} alt="Average icon" className="metric-icon-image" />
        </div>
        <div className="metric-text">
          <span>Average</span>
          <strong className={getAuditScoreClass(data?.average)}>{Number(data?.average || 0).toFixed(1)}</strong>
        </div>
      </div>
      <div className="metric-divider" />
      <div className="summary-metric">
        <div className="metric-icon">
          <img src={lastAuditIcon} alt="Last audit icon" className="metric-icon-image" />
        </div>
        <div className="metric-text">
          <span>Last audit</span>
          <strong className="metric-value">
            <span className="value">{data?.lastAudit || 0}</span>{' '}
            <span className="unit">days ago</span>
          </strong>
        </div>
      </div>
    </div>
  </div>
);

const AuditSection = ({
  expanded,
  onToggle,
  onDownload,
  onMore,
  onMoreMenuItems,
  showMoreAction,
  showActions,
  canEditAudit,
  canDeleteAudit,
  auditScoreData,
  getAuditSortClass,
  auditSort,
  getAriaSort,
  handleAuditSort,
  auditSortedData,
  handleAuditEdit,
  handleAuditDelete,
  isAuditEmpty,
  isLoading,
}) => (
  <ExpandableSection
    title="Audit score"
    expanded={expanded}
    onToggle={onToggle}
    onDownload={onDownload}
    onMore={onMore}
    onMoreMenuItems={onMoreMenuItems}
    showMoreAction={showMoreAction}
    isLoading={isLoading}
  >
    <div className="section-content audit-section">
      <div className="summary-row display-none">
        <SummaryCard title="Process compliance" data={auditScoreData?.process} />
        <SummaryCard title="Quality" data={auditScoreData?.quality} />
        <SummaryCard title="Technology" data={auditScoreData?.technology} />
      </div>

      <div className="audit-table-wrapper">
        <div className="audit-table-scroll">
          <table className="audit-data-table">
            <thead>
              <tr>
                <th
                  className={`th-cell sortable ${getAuditSortClass('processDate')}`}
                  aria-sort={getAriaSort(auditSort.key, 'processDate', auditSort.direction)}
                >
                  <button type="button" className="th-content" onClick={() => handleAuditSort('processDate')}>
                    <span>Process compliance audit date</span>
                    <span className="sort-icon" />
                  </button>
                </th>
                <th
                  className={`th-cell sortable ${getAuditSortClass('processScore')}`}
                  aria-sort={getAriaSort(auditSort.key, 'processScore', auditSort.direction)}
                >
                  <button type="button" className="th-content" onClick={() => handleAuditSort('processScore')}>
                    <span>Process compliance</span>
                    <span className="sort-icon" />
                  </button>
                </th>
                <th
                  className={`th-cell sortable ${getAuditSortClass('qualityDate')}`}
                  aria-sort={getAriaSort(auditSort.key, 'qualityDate', auditSort.direction)}
                >
                  <button type="button" className="th-content" onClick={() => handleAuditSort('qualityDate')}>
                    <span>Quality Audit date</span>
                    <span className="sort-icon" />
                  </button>
                </th>
                <th
                  className={`th-cell sortable ${getAuditSortClass('qualityScore')}`}
                  aria-sort={getAriaSort(auditSort.key, 'qualityScore', auditSort.direction)}
                >
                  <button type="button" className="th-content" onClick={() => handleAuditSort('qualityScore')}>
                    <span>Quality</span>
                    <span className="sort-icon" />
                  </button>
                </th>
                <th
                  className={`th-cell sortable ${getAuditSortClass('technologyDate')}`}
                  aria-sort={getAriaSort(auditSort.key, 'technologyDate', auditSort.direction)}
                >
                  <button type="button" className="th-content" onClick={() => handleAuditSort('technologyDate')}>
                    <span>Technology Audit date</span>
                    <span className="sort-icon" />
                  </button>
                </th>
                <th
                  className={`th-cell sortable ${getAuditSortClass('technologyScore')}`}
                  aria-sort={getAriaSort(auditSort.key, 'technologyScore', auditSort.direction)}
                >
                  <button type="button" className="th-content" onClick={() => handleAuditSort('technologyScore')}>
                    <span>Technology</span>
                    <span className="sort-icon" />
                  </button>
                </th>
                {showActions ? (
                  <th className="th-cell th-actions">
                    <div className="th-content th-actions-content">
                      <span>Actions</span>
                    </div>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {auditSortedData.map((row, idx) => (
                <tr key={row.id || idx} className="tr-data">
                  <td className="td-cell"><DateCell date={row.processDate} day={row.processDay} /></td>
                  <td className="td-cell">
                    <span className={getAuditScoreClass(row.processScore)}>
                      {formatAuditScoreDisplay(row.processScore)}
                    </span>
                  </td>
                  <td className="td-cell"><DateCell date={row.qualityDate} day={row.qualityDay} /></td>
                  <td className="td-cell">
                    <span className={getAuditScoreClass(row.qualityScore)}>
                      {formatAuditScoreDisplay(row.qualityScore)}
                    </span>
                  </td>
                  <td className="td-cell"><DateCell date={row.technologyDate} day={row.technologyDay} /></td>
                  <td className="td-cell">
                    <span className={getAuditScoreClass(row.technologyScore)}>
                      {formatAuditScoreDisplay(row.technologyScore)}
                    </span>
                  </td>
                  {showActions ? (
                    <td className="td-cell td-actions">
                      <ActionButtons
                        onEdit={canEditAudit ? () => handleAuditEdit(row) : null}
                        onDelete={canDeleteAudit ? () => handleAuditDelete(row) : null}
                      />
                    </td>
                  ) : null}
                </tr>
              ))}
              {isAuditEmpty ? (
                <tr className="tr-data tr-empty">
                  <td className="td-cell td-empty" colSpan={showActions ? 7 : 6}>No audit records</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </ExpandableSection>
);

SummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.object,
};

AuditSection.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  onMore: PropTypes.func,
  onMoreMenuItems: PropTypes.array,
  showMoreAction: PropTypes.bool,
  showActions: PropTypes.bool,
  canEditAudit: PropTypes.bool,
  canDeleteAudit: PropTypes.bool,
  auditScoreData: PropTypes.object.isRequired,
  getAuditSortClass: PropTypes.func.isRequired,
  auditSort: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string,
  }).isRequired,
  getAriaSort: PropTypes.func.isRequired,
  handleAuditSort: PropTypes.func.isRequired,
  auditSortedData: PropTypes.array.isRequired,
  handleAuditEdit: PropTypes.func.isRequired,
  handleAuditDelete: PropTypes.func.isRequired,
  isAuditEmpty: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
};

AuditSection.defaultProps = {
  onDownload: null,
  onMore: null,
  onMoreMenuItems: [],
  showMoreAction: true,
  showActions: true,
  canEditAudit: true,
  canDeleteAudit: true,
  isLoading: false,
};

export default AuditSection;
